/**
 * Payment Required Modal Component
 * 
 * Displays a modal when users try to access premium features without an active subscription.
 * Handles HTTP 402 (Payment Required) responses from the backend.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from '../../i18n/hooks';
import { useSubscriptionActions } from '../hooks/useSubscription';
import { Button } from '../../ui';
import './PaymentRequiredModal.css';

/**
 * Payment Required Modal Component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Function} props.onActivate - Function to call when user activates subscription
 * @param {string} props.featureName - Name of the feature that requires subscription
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Payment required modal component
 */
const PaymentRequiredModal = ({
  isOpen = false,
  onClose,
  onActivate,
  featureName = '',
  className = ''
}) => {
  const { t } = useTranslation();
  const { activate } = useSubscriptionActions();
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState(null);

  /**
   * Handle subscription activation
   */
  const handleActivate = useCallback(async () => {
    setIsActivating(true);
    setActivationError(null);
    
    try {
      const { data, error } = await activate();
      if (error) {
        setActivationError(error.message || t('subscription.errors.activationFailed'));
      } else {
        // Close modal and notify parent
        onClose();
        onActivate && onActivate(data);
      }
    } catch (err) {
      setActivationError(err.message || t('subscription.errors.networkError'));
    } finally {
      setIsActivating(false);
    }
  }, [activate, onClose, onActivate, t]);

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    setActivationError(null);
    onClose();
  }, [onClose]);

  /**
   * Handle click outside modal
   */
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  /**
   * Handle escape key
   */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.classList.add('modal-open');
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className={`payment-required-modal-overlay ${className}`}
      onClick={handleOverlayClick}
    >
      <div className="payment-required-modal">
        <div className="payment-required-modal-content">
          {/* Header */}
          <div className="payment-required-modal-header">
            <div className="payment-required-modal-icon">
              <svg className="payment-required-lock-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
              </svg>
            </div>
            <h2 className="payment-required-modal-title">
              {t('subscription.paymentRequired.title')}
            </h2>
            <p className="payment-required-modal-message">
              {featureName 
                ? t('subscription.paymentRequired.messageWithFeature', { feature: featureName })
                : t('subscription.paymentRequired.message')
              }
            </p>
          </div>

          {/* Features List */}
          <div className="payment-required-features">
            <h3 className="payment-required-features-title">
              {t('subscription.paymentRequired.featuresTitle')}
            </h3>
            <ul className="payment-required-features-list">
              <li className="payment-required-feature-item">
                <svg className="payment-required-check-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t('subscription.paymentRequired.features.surebets')}
              </li>
              <li className="payment-required-feature-item">
                <svg className="payment-required-check-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t('subscription.paymentRequired.features.calculator')}
              </li>
              <li className="payment-required-feature-item">
                <svg className="payment-required-check-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t('subscription.paymentRequired.features.realTime')}
              </li>
              <li className="payment-required-feature-item">
                <svg className="payment-required-check-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                {t('subscription.paymentRequired.features.support')}
              </li>
            </ul>
          </div>

          {/* Pricing */}
          <div className="payment-required-pricing">
            <div className="payment-required-price">
              <span className="payment-required-price-amount">15 €</span>
              <span className="payment-required-price-period">{t('subscription.paymentRequired.perMonth')}</span>
            </div>
            <p className="payment-required-pricing-note">
              {t('subscription.paymentRequired.pricingNote')}
            </p>
          </div>

          {/* Error Message */}
          {activationError && (
            <div className="payment-required-modal-error">
              <p className="payment-required-error-text">
                {activationError}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="payment-required-modal-actions">
            <button 
              className="payment-required-modal-btn payment-required-modal-btn-secondary"
              onClick={handleClose}
              disabled={isActivating}
            >
              {t('subscription.paymentRequired.later')}
            </button>
            <button 
              className="payment-required-modal-btn payment-required-modal-btn-primary"
              onClick={handleActivate}
              disabled={isActivating}
            >
              {isActivating ? t('subscription.actions.activating') : t('subscription.paymentRequired.activateNow')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

PaymentRequiredModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onActivate: PropTypes.func,
  featureName: PropTypes.string,
  className: PropTypes.string,
};

export default PaymentRequiredModal;
